import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { getUser, getUserStatus } from "../../../../pages/userSlice";
import { useSelector } from "react-redux";
import { CODES } from "../../../../consts/codes";
import CustomSkeleton from "../../../common/CustomSkeleton";
import { GetChartsAndIndicators } from "../../../../services/user/infonavit/homeRequests";
import { NewCardsSection } from "./newCardsSection";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Line, Bar } from "react-chartjs-2";
import Box from "@mui/material/Box";
import { formatMxCurrency } from "../../../../utils/functions";

export const HomeCharts = ({ nrpList }) => {
  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  const [cardList, setCardList] = useState([]);
  const [chartList, setChartList] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  const chartColors = ["rgba(141, 81, 218, 1)", "rgba(252, 176, 69, 1)"];
  const options = {
    indexAxis: "y",
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    responsive: true,

    plugins: {
      legend: {
        display: false,
        position: "bottom",
      },

      tooltip: {
        callbacks: {
          label: function (context) {
            let value = context.dataset.data[context.dataIndex].toFixed(3);

            return value;
          },
        },
      },
    },

    // scales: {
    //   x: {
    //     ticks: {
    //       callback: function (value) {
    //         return value + "M";
    //       },
    //     },
    //   },
    // },
  };

  /**
   * Carga las gráficas e indicadores
   */
  useEffect(() => {
    if (userStatus !== "succeeded") return;

    if (nrpList.length > 0) fetchChartsAndIndicators();
  }, [userStatus, nrpList]);

  /**
   * Consulta la información para los indicadores y gráficas
   */
  const fetchChartsAndIndicators = async () => {
    try {
      setIsLoading(true);

      const payload = {
        nrps: nrpList,
        mainUser: userInfo.mainUser,
      };

      const {
        status,
        data: { responseStatus, response },
      } = await GetChartsAndIndicators(payload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        const cards = response?.cards ?? [];
        // money format applied to total facDescuento and total monDescuento cards
        if (cards[0] && cards[1]) {
          cards[0].value = formatMxCurrency(cards[0]?.value ?? 0);
          cards[1].value = formatMxCurrency(cards[1]?.value ?? 0);
        }
        setCardList(cards);
        setChartList(response?.cardsGraphics ?? []);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setIsLoading(false);
    }
  };

  // Charts templates
  const barChart = (chart, index) => {
    const chartName = chart?.name ?? "";

    const chartLabels = chart?.details.map((item) => [
      item?.name,
      formatMxCurrency(item?.value ?? 0),
    ]);
    const chartValues = chart?.details.map((item) => item?.value);
    const chartData = {
      labels: chartLabels,
      datasets: [
        {
          label: "",
          data: chartValues,
          backgroundColor: chartColors,
        },
      ],
    };

    return (
      <Grid item xs={12} lg={6} key={index}>
        <Card
          style={{
            borderRadius: "16px",
            boxShadow: "none",
          }}
        >
          <CardContent className="card-content">
            <Box className="box-content__bar">
              <label className="subheading__text-black">{chartName}</label>

              <Bar options={options} data={chartData} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const lineGraphChart = (chart, index) => {
    const lineGraphs = {
      responsive: true,
      plugins: {
        legend: {
          position: "bottom",
        },
      },
    };

    const randomChartColors = [
      "rgba(141, 81, 218, 0.8)", // Morado
      "rgba(252, 176, 69, 0.8)", // Amarillo
      "rgba(44, 160, 44, 0.8)", // Verde
      "rgba(214, 39, 40, 0.8)", // Rojo
      "rgba(31, 119, 180, 0.8)", // Azul
      "rgba(255, 127, 14, 0.8)", // Naranja
      "rgba(140, 86, 75, 0.8)", // Marrón
      "rgba(227, 119, 194, 0.8)", // Rosa
      "rgba(127, 127, 127, 0.8)", // Gris
      "rgba(188, 189, 34, 0.8)", // Lima/Verde oliva
      "rgba(23, 190, 207, 0.8)", // Cian
      "rgba(174, 199, 232, 0.8)", // Azul claro
      "rgba(255, 187, 120, 0.8)", // Melocotón/Naranja claro
      "rgba(152, 223, 138, 0.8)", // Verde claro
      "rgba(255, 152, 150, 0.8)", // Salmón/Rojo claro
      "rgba(197, 176, 213, 0.8)", // Lavanda
      "rgba(196, 156, 148, 0.8)", // Marrón claro
      "rgba(247, 182, 210, 0.8)", // Rosa claro
      "rgba(199, 199, 199, 0.8)", // Gris claro
      "rgba(219, 219, 141, 0.8)", // Beige/Amarillo claro
      "rgba(158, 218, 229, 0.8)", // Azul celeste
    ];

    const chartName = chart?.name ?? "";

    const firstChartElement = chart?.details[0] ?? false;
    const chartLabels = firstChartElement
      ? firstChartElement?.details.map((item) => item?.name)
      : [];
    const chartValues = chart?.details.map((item, index) => {
      return {
        label: item?.name,
        data: item?.details.map((subItem) => subItem?.value),
        borderColor: randomChartColors[index] ?? randomChartColors[0],
        backgroundColor: randomChartColors[index] ?? randomChartColors[0],
      };
    });

    const chartData = {
      labels: chartLabels,
      datasets: chartValues,
    };

    return (
      <Grid item xs={12} lg={6} key={index}>
        <Card
          style={{
            borderRadius: "16px",
            boxShadow: "none",
          }}
        >
          <CardContent className="card-content">
            <Box className="box-content__bar">
              <label className="subheading__text-black">{chartName}</label>

              <Line options={lineGraphs} data={chartData} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <>
      {/* Indicators */}
      <Grid item xs={12}>
        {isLoading ? (
          <CustomSkeleton />
        ) : (
          <>
            <Grid container spacing={2} justifyContent="center">
              <NewCardsSection cardList={cardList} t={t} />
            </Grid>
          </>
        )}
      </Grid>

      {/* Charts */}
      <Grid item xs={12}>
        {isLoading ? (
          <CustomSkeleton />
        ) : (
          <>
            <Grid container spacing={1}>
              {chartList.map((chart, index) => {
                const chartType = chart?.type;

                return chartType === "BAR" ? (
                  barChart(chart, index)
                ) : chartType === "LINE_GRAPHS" ? (
                  lineGraphChart(chart, index)
                ) : (
                  <></>
                );
              })}
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};
